import { c as _c } from "react/compiler-runtime";
import dynamic from "next/dynamic";
import { Suspense, useEffect, type ReactNode } from "react";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { useActiveGithubOrgForUser } from "@/hooks/useConnectedGithubOrgs";
import { deferNavigationSettle } from "@/hooks/useNavigationTimingReporting";
import { isCodesmithExtensionEmbed } from "@/lib/codesmithExtensionEmbed";
import { SessionSkeleton } from "./SessionSkeleton";

// Codesmith renders the tab title once mounted (the session's title when it
// has one), so exactly one <title> exists at a time: this one stands in
// until then.
function ShellFallback() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <><title>[code]smith</title><SessionSkeleton /></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

// Codesmith owns browser-only draft persistence and realtime subscriptions,
// so keep its render boundary client-only.
const Codesmith = dynamic(() => import("./Codesmith"), {
  ssr: false,
  loading: () => <ShellFallback />
});
function isSessionsPath(pathname: string): boolean {
  return /^(\/\[org\])?\/sessions(\/|$)/.test(pathname);
}

// useActiveGithubOrgForUser suspends, so it lives below the shell's Suspense
// boundary; calling it in the layout component itself would send the
// fallback to the persistent app layout's boundary instead of the
// SessionSkeleton.
function ShellContent(t0) {
  const $ = _c(12);
  const {
    children
  } = t0;
  const router = useRouter();
  const {
    activeGithubOrg,
    codesmithEnabled
  } = useActiveGithubOrgForUser();
  let t1;
  if ($[0] !== router.pathname) {
    t1 = isSessionsPath(router.pathname);
    $[0] = router.pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isSubpage = !t1;
  let t2;
  let t3;
  if ($[2] !== codesmithEnabled || $[3] !== router) {
    t2 = () => {
      if (!router.isReady) {
        return;
      }
      if (codesmithEnabled) {
        return;
      }
      if (isCodesmithExtensionEmbed(router.query.embed)) {
        return;
      }
      const org = router.query.org;
      if (!org) {
        return;
      }
      router.replace(`/${org}/runs/jobs`);
    };
    t3 = [router, router.isReady, router.query.org, router.query.embed, codesmithEnabled];
    $[2] = codesmithEnabled;
    $[3] = router;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  if (!codesmithEnabled) {
    if (isCodesmithExtensionEmbed(router.query.embed)) {
      let t4;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <><title>[code]smith</title><div className="flex h-full items-center justify-center p-6 text-center type-body-s-regular text-neutral-500 dark:text-neutral-400">[code]smith is not enabled for this organization.</div></>;
        $[6] = t4;
      } else {
        t4 = $[6];
      }
      return t4;
    }
    let t4;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <title>[code]smith</title>;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  let t4;
  if ($[8] !== activeGithubOrg || $[9] !== children || $[10] !== isSubpage) {
    t4 = <Codesmith key={activeGithubOrg} isSubpage={isSubpage}>{children}</Codesmith>;
    $[8] = activeGithubOrg;
    $[9] = children;
    $[10] = isSubpage;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}

/**
 * The Codesmith app shell (session sidebar + main pane), mounted once by the
 * `(codesmith)` route group layout so navigating between the sessions,
 * automations, and MCP connections routes swaps only the main pane. On the
 * sessions route Codesmith owns the main pane itself; every other route
 * renders its page as `children` inside the shell.
 */
export function CodesmithShell(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  deferNavigationSettle();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ShellFallback />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <Suspense fallback={t1}><ShellContent>{children}</ShellContent></Suspense>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}